import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseReady } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session first
    const storedSession = localStorage.getItem('admin-session');
    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession);
        // Check if session is still valid (not expired)
        if (parsedSession.expires_at > Math.floor(Date.now() / 1000)) {
          setSession(parsedSession);
          setUser(parsedSession.user);
          setLoading(false);
          return;
        } else {
          // Session expired, remove it
          localStorage.removeItem('admin-session');
        }
      } catch (e) {
        // Invalid stored session, remove it
        localStorage.removeItem('admin-session');
      }
    }

    // Get initial session from Supabase
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    // If Supabase is configured, always use real Supabase auth so RLS works
    if (isSupabaseReady) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error };
    }

    // Dev-only mocks when Supabase is NOT configured (local demo without DB)
    if (email === 'admin@yallacompare.com' && password === 'admin123') {
      const mockUser = {
        id: 'dev-admin-user',
        email: 'admin@yallacompare.com',
        created_at: new Date().toISOString(),
        app_metadata: { role: 'system-admin' },
        user_metadata: {},
        aud: 'authenticated',
        role: 'authenticated'
      } as User;

      const mockSession = {
        access_token: 'dev-token',
        refresh_token: 'dev-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;

      setUser(mockUser);
      setSession(mockSession);
      localStorage.setItem('admin-session', JSON.stringify(mockSession));
      return { error: null };
    }

    if ((email === 'audits' || email === 'audits@yallacompare.com') && password === 'audits123') {
      const mockUser = {
        id: 'audits-tenant-admin',
        email: 'audits@yallacompare.com',
        created_at: new Date().toISOString(),
        app_metadata: { role: 'tenant-audits-admin', tenant: 'audits' },
        user_metadata: {},
        aud: 'authenticated',
        role: 'authenticated'
      } as User;

      const mockSession = {
        access_token: 'audits-dev-token',
        refresh_token: 'audits-dev-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;

      setUser(mockUser);
      setSession(mockSession);
      localStorage.setItem('admin-session', JSON.stringify(mockSession));
      return { error: null };
    }

    if ((email === 'incorpify' || email === 'incorpify@yallacompare.com') && password === 'incorpify123') {
      const mockUser = {
        id: 'incorpify-tenant-admin',
        email: 'incorpify@yallacompare.com',
        created_at: new Date().toISOString(),
        app_metadata: { role: 'tenant-incorpify-admin', tenant: 'incorpify' },
        user_metadata: {},
        aud: 'authenticated',
        role: 'authenticated'
      } as User;

      const mockSession = {
        access_token: 'incorpify-dev-token',
        refresh_token: 'incorpify-dev-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;

      setUser(mockUser);
      setSession(mockSession);
      localStorage.setItem('admin-session', JSON.stringify(mockSession));
      return { error: null };
    }

    if ((email === 'dubaicommercity' || email === 'dcc@yallacompare.com') && password === 'dcc123') {
      const mockUser = {
        id: 'dcc-tenant-admin',
        email: 'dcc@yallacompare.com',
        created_at: new Date().toISOString(),
        app_metadata: { role: 'tenant-dcc-admin', tenant: 'dubaicommercity' },
        user_metadata: {},
        aud: 'authenticated',
        role: 'authenticated'
      } as User;

      const mockSession = {
        access_token: 'dcc-dev-token',
        refresh_token: 'dcc-dev-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session;

      setUser(mockUser);
      setSession(mockSession);
      return { error: null };
    }

    return { error: new Error('Invalid credentials') as unknown as Error };
  };

  const signOut = async () => {
    // Clear local state
    setUser(null);
    setSession(null);

    // Clear stored session
    localStorage.removeItem('admin-session');

    // Also sign out from Supabase if there's a real session
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
